import React, { useState } from 'react';
import { XMarkIcon, PlusIcon, PencilSquareIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/outline';
import scheduleService, { errText } from '../../services/scheduleService';
import { doctorName, initials } from './scheduleUtils';

/**
 * Admin: partnership / coverage groups. A group is a saved overlay — picking
 * it in the schedule shows just those doctors side by side.
 */
const GroupsModal = ({ groups, doctors, colours, onClose, onChanged }) => {
  const [editing, setEditing] = useState(null); // { gid, gname, members: Set }
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const byId = Object.fromEntries(doctors.map((d) => [d.uid, d]));

  const toggle = (uid) =>
    setEditing((g) => {
      const members = new Set(g.members);
      if (members.has(uid)) members.delete(uid); else members.add(uid);
      return { ...g, members };
    });

  const save = async () => {
    setErr('');
    if (!editing.gname.trim()) return setErr('Give the group a name.');
    setSaving(true);
    try {
      await scheduleService.saveGroup({ gid: editing.gid, gname: editing.gname.trim(), members: [...editing.members] });
      await onChanged();
      setEditing(null);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete the group “${g.gname}”? Doctors and their sessions are not affected.`)) return;
    try {
      await scheduleService.deleteGroup(g.gid);
      await onChanged();
    } catch (e) {
      setErr(errText(e));
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-brand-deep/50 p-4 backdrop-blur-sm" onMouseDown={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-brand-deep">{editing ? (editing.gid ? 'Edit group' : 'New group') : 'Partnership & coverage groups'}</h2>
            {!editing && (
              <p className="text-sm text-slate-500">
                Pick a group in the schedule to overlay just those doctors. Doctors with “Can edit partners’ schedules” (User Management) can edit sessions for the others in their groups.
              </p>
            )}
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {editing ? (
            <div className="flex flex-col gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-[0.8rem] font-semibold text-slate-600">Group name</span>
                <input
                  autoFocus
                  value={editing.gname}
                  onChange={(e) => setEditing((g) => ({ ...g, gname: e.target.value }))}
                  placeholder="e.g. Sunninghill Partnership"
                  className="w-full rounded-lg border border-slate-300 p-2.5 text-sm outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40"
                />
              </label>
              <div>
                <div className="mb-1.5 text-[0.8rem] font-semibold text-slate-600">Doctors ({editing.members.size})</div>
                <div className="flex flex-col divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {doctors.map((d) => {
                    const on = editing.members.has(d.uid);
                    return (
                      <button key={d.uid} type="button" onClick={() => toggle(d.uid)} className="flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50">
                        <span className="grid h-7 w-7 place-items-center rounded-full text-[10px] font-bold text-white" style={{ background: colours[d.uid] }}>
                          {initials(d)}
                        </span>
                        <span className="flex-1 text-sm text-slate-700">{doctorName(d)}</span>
                        <span className={`grid h-5 w-5 place-items-center rounded border ${on ? 'border-brand-navy bg-brand-navy text-white' : 'border-slate-300'}`}>
                          {on && <CheckIcon className="h-3.5 w-3.5" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : groups.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No groups yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-slate-100">
              {groups.map((g) => (
                <li key={g.gid} className="flex items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-700">{g.gname}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {g.members.filter((uid) => byId[uid]).map((uid) => (
                        <span key={uid} className="rounded-full px-2 py-0.5 text-[11px] font-medium text-white" style={{ background: colours[uid] }}>
                          {doctorName(byId[uid])}
                        </span>
                      ))}
                      {g.members.length === 0 && <span className="text-xs text-slate-400">No doctors</span>}
                    </div>
                  </div>
                  <button onClick={() => setEditing({ gid: g.gid, gname: g.gname, members: new Set(g.members) })} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-brand-navy" title="Edit">
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>
                  <button onClick={() => remove(g)} className="rounded-md p-2 text-slate-500 hover:bg-red-50 hover:text-red-600" title="Delete">
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {err && <div className="mt-3 text-sm text-red-600">{err}</div>}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          {editing ? (
            <>
              <button onClick={() => { setEditing(null); setErr(''); }} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">Back</button>
              <button onClick={save} disabled={saving} className="rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-60">
                {saving ? 'Saving…' : 'Save group'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing({ gid: null, gname: '', members: new Set() })}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95"
            >
              <PlusIcon className="h-5 w-5" /> New group
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupsModal;
